import { useTranslation } from 'react-i18next'

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import { Trans } from '@/components/Trans'
import { StorageBillingMode } from '@/utils/storage-billing'

interface AnnouncementBannerProps {
  personalMode?: StorageBillingMode
  teamSharedMode?: StorageBillingMode
  isTeamContext: boolean
}

function getAffectedResources(mode?: StorageBillingMode) {
  if (mode?.imageOnSpaceBilling && mode.snapshotOnSpaceBilling) return 'both'
  if (mode?.imageOnSpaceBilling) return 'images'
  if (mode?.snapshotOnSpaceBilling) return 'snapshots'
  return null
}

export const AnnouncementBanner = ({
  personalMode,
  teamSharedMode,
  isTeamContext,
}: AnnouncementBannerProps) => {
  const { t } = useTranslation()
  const personalResources = getAffectedResources(personalMode)
  const sharedResources = isTeamContext ? getAffectedResources(teamSharedMode) : null
  const combinedResources = getAffectedResources({
    imageOnSpaceBilling:
      !!personalMode?.imageOnSpaceBilling ||
      (isTeamContext && !!teamSharedMode?.imageOnSpaceBilling),
    snapshotOnSpaceBilling:
      !!personalMode?.snapshotOnSpaceBilling ||
      (isTeamContext && !!teamSharedMode?.snapshotOnSpaceBilling),
  })
  if (!combinedResources) return null

  const title = t('header.announcementTitle', {
    resources: t(`header.announcementResources.${combinedResources}`),
  })
  const sections = [
    {
      scope: 'personal',
      resources: personalResources,
      label: isTeamContext
        ? t('header.announcementTeamAccount')
        : t('header.announcementPersonalAccount'),
    },
    {
      scope: 'shared',
      resources: sharedResources,
      label: t('header.announcementTeamShared'),
    },
  ].filter((section) => section.resources)

  return (
    <div
      className="
        group relative pointer-events-auto
        flex flex-col
        w-full min-w-0 max-w-[620px]
        min-h-[var(--announcement-row-height,40px)]
      "
    >
      <div className="flex items-center justify-center gap-2 rounded-xl gradient-row px-4 py-2 min-h-[40px]">
        <div className="shrink-0">📢</div>
        <div className="min-w-0 max-w-[550px] t-sb text-primary-reverse text-left text-ellipsis overflow-hidden whitespace-nowrap">
          {title}
        </div>
        <div className="shrink-0 text-primary-reverse/65">
          <ArrowDownIcon className="w-4 h-4 transition-transform duration-300 group-hover:rotate-180" />
        </div>
      </div>

      <div
        className="
          absolute top-full left-1/2 -translate-x-1/2
          w-[min(620px,calc(100vw-32px))]
          grid grid-rows-[0fr] group-hover:grid-rows-[1fr]
          opacity-0 group-hover:opacity-100
          pointer-events-none group-hover:pointer-events-auto
          transition-[grid-template-rows,opacity] duration-300
        "
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mt-2 rounded-xl gradient-row px-4 py-3 text-left t-sb text-primary-reverse">
            <div className="mb-2 border-b border-white/30 pb-2">{title}</div>
            <div className="flex flex-col gap-3">
              {sections.map(({ scope, resources, label }) => (
                <div key={scope}>
                  <div className="mb-1 font-bold">{label}</div>
                  <Trans
                    i18nKey="header.announcementContent"
                    values={{ resources: t(`header.announcementResources.${resources}`) }}
                    components={{ strong: <strong /> }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
